import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Tv, Eye, EyeOff } from 'lucide-react';
import { totemModeService } from '../../../shared/services/totemModeService';

const LoginPage = () => {
    const navigate = useNavigate();
    const { login, loading, error } = useAuth();
    const passwordInputRef = useRef<HTMLInputElement>(null);

    const [credentials, setCredentials] = useState({
        login: '',
        password: '',
        lojaId: 1,
        empresa: process.env.REACT_APP_EMPRESA || ''
    });

    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Carregar credenciais salvas ao iniciar
    React.useEffect(() => {
        const savedLogin = localStorage.getItem('saved_login');
        const savedPassword = localStorage.getItem('saved_password');
        const savedLojaId = localStorage.getItem('saved_lojaId');
        const savedEmpresa = localStorage.getItem('auth_empresa');

        // Carregar login, loja e empresa sempre que existirem
        if (savedLogin || savedLojaId || savedEmpresa) {
            setCredentials(prev => ({
                ...prev,
                login: savedLogin || prev.login,
                lojaId: savedLojaId ? parseInt(savedLojaId) : prev.lojaId,
                empresa: savedEmpresa || prev.empresa
            }));
        }

        // Carregar senha apenas se existir e marcar checkbox
        if (savedPassword) {
            setCredentials(prev => ({
                ...prev,
                password: savedPassword
            }));
            setRememberMe(true);
        }
    }, []);

    // Sincronizar valor do ref apenas no carregamento inicial (quando há senha salva)
    const [initialPasswordLoaded, setInitialPasswordLoaded] = useState(false);
    useEffect(() => {
        if (passwordInputRef.current && credentials.password && !initialPasswordLoaded) {
            // Definir o valor no input quando carregar do localStorage
            passwordInputRef.current.value = credentials.password;
            setInitialPasswordLoaded(true);
            // Remover o atributo value imediatamente
            setTimeout(() => {
                if (passwordInputRef.current) {
                    passwordInputRef.current.removeAttribute('value');
                }
            }, 0);
        }
    }, [credentials.password, initialPasswordLoaded]);

    // Remover value do DOM periodicamente para não aparecer no Elements
    useEffect(() => {
        const interval = setInterval(() => {
            if (passwordInputRef.current) {
                // Remover o atributo value do DOM, mas manter o valor funcional
                passwordInputRef.current.removeAttribute('value');
            }
        }, 50); // Verificar a cada 50ms

        return () => clearInterval(interval);
    }, []);

    // Handlers para prevenir cópia e visualização
    const handlePasswordCopy = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        e.clipboardData.setData('text/plain', '');
        return false;
    };

    const handlePasswordCut = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        return false;
    };

    const handlePasswordContextMenu = (e: React.MouseEvent<HTMLInputElement>) => {
        e.preventDefault();
        return false;
    };

    const handlePasswordPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        // Permitir colar, mas prevenir cópia
        const pastedText = e.clipboardData.getData('text');
        if (pastedText) {
            setCredentials(prev => ({
                ...prev,
                password: pastedText
            }));
            if (passwordInputRef.current) {
                passwordInputRef.current.value = pastedText;
            }
        }
        e.preventDefault();
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = e.target.value;
        setCredentials(prev => ({
            ...prev,
            password: newPassword
        }));
    };

    const handlePasswordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Prevenir apenas Ctrl+C (copiar) e Ctrl+X (recortar)
        // Permitir Ctrl+A (selecionar tudo) e Ctrl+V (colar)
        if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C' || e.key === 'x' || e.key === 'X')) {
            e.preventDefault();
            return false;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            await login(credentials);

            // Desativar modo totem ao fazer login (se estiver ativo)
            totemModeService.disableTotemMode();

            // Sempre salvar login, loja e empresa
            localStorage.setItem('saved_login', credentials.login);
            localStorage.setItem('saved_lojaId', credentials.lojaId.toString());
            // A empresa já é salva pelo apiService, mas garantimos aqui também
            localStorage.setItem('auth_empresa', credentials.empresa);

            // Salvar senha apenas se "Lembrar senha" estiver marcado
            if (rememberMe) {
                localStorage.setItem('saved_password', credentials.password);
            } else {
                // Remover apenas a senha se o checkbox não estiver marcado
                localStorage.removeItem('saved_password');
            }

            navigate('/screens');
        } catch (err) {
            // Erro já tratado pelo hook
        }
    };

    return (
        <div className="fixed inset-0 w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 overflow-y-auto">
            <div className="max-w-md w-full mx-4">
                <div className="bg-white rounded-lg shadow-lg p-8">
                    <div className="flex items-center justify-center mb-6">
                        <img
                            src={`${process.env.PUBLIC_URL || ''}/logo.webp`}
                            alt="Market Media"
                            className="h-16 w-auto max-w-[220px] object-contain"
                        />
                    </div>

                    <h2 className="text-3xl font-bold text-center text-gray-800 mb-2">
                        Market Media
                    </h2>
                    <p className="text-center text-gray-600 mb-6">
                        Faça login para acessar o sistema
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Empresa
                            </label>
                            <input
                                type="text"
                                value={credentials.empresa}
                                onChange={(e) => setCredentials(prev => ({
                                    ...prev,
                                    empresa: e.target.value
                                }))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                                disabled={loading}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Usuário
                            </label>
                            <input
                                type="text"
                                value={credentials.login}
                                onChange={(e) => setCredentials(prev => ({
                                    ...prev,
                                    login: e.target.value.toUpperCase()
                                }))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                                placeholder="Digite seu usuário"
                                required
                                disabled={loading}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Senha
                            </label>
                            <div className="relative">
                                <input
                                    ref={passwordInputRef}
                                    type={showPassword ? 'text' : 'password'}
                                    onChange={handlePasswordChange}
                                    onCopy={handlePasswordCopy}
                                    onCut={handlePasswordCut}
                                    onPaste={handlePasswordPaste}
                                    onContextMenu={handlePasswordContextMenu}
                                    onKeyDown={handlePasswordKeyDown}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                                    placeholder="Digite sua senha"
                                    autoComplete="new-password"
                                    required
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Loja
                            </label>
                            <input
                                type="number"
                                value={credentials.lojaId}
                                onChange={(e) => setCredentials(prev => ({
                                    ...prev,
                                    lojaId: parseInt(e.target.value) || 1
                                }))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="flex items-center">
                            <input
                                id="remember-me"
                                name="remember-me"
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                className="w-4 h-4 accent-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                disabled={loading}
                            />
                            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                                Lembrar senha
                            </label>
                        </div>

                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? 'Entrando...' : 'Entrar'}
                        </button>
                    </form>

                    <div className="mt-6 pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={() => navigate('/televisao')}
                            className="w-full py-3 px-4 bg-gray-200 text-gray-800 font-medium rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors flex items-center justify-center gap-2"
                        >
                            <Tv size={20} />
                            Gerar Código de Totem
                        </button>
                    </div>

                    <div className="mt-6 text-center text-sm text-gray-600">
                        <p>Ambiente: <span className="font-semibold">{credentials.empresa || 'Não definido'}</span></p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
